import { z } from 'zod/v4';
import { browserCliHandler } from '../../browser/cli-handler';
import { createTool } from '../../tools';
import { WORKSPACE_TOOLS } from '../constants';
import { SandboxFeatureNotSupportedError } from '../errors';
import {
  DEFAULT_MAX_RETAINED_PROCESS_OUTPUT_BYTES,
  RetainedOutputBuffer,
} from '../sandbox/process-manager/process-handle';
import { coerceNumericString, emitWorkspaceMetadata, requireSandbox } from './helpers';
import { DEFAULT_TAIL_LINES, truncateOutput, sandboxToModelOutput } from './output-helpers';
import { startWorkspaceSpan } from './tracing';

/**
 * Base input schema for execute_command (no background param).
 * Extended with `background` in tools.ts when sandbox.processes exists.
 */
export const executeCommandInputSchema = z.object({
  command: z
    .string()
    .describe('The shell command to execute (e.g., "npm install", "ls -la src/", "cat file.txt | grep error")'),
  timeout: z
    .preprocess(coerceNumericString, z.number())
    .nullish()
    .describe('Maximum execution time in seconds. Example: 60 for 1 minute.'),
  cwd: z.string().nullish().describe('Working directory for the command'),
  tail: z
    .preprocess(coerceNumericString, z.number().int())
    .nullish()
    .describe(
      `For foreground commands: limit output to the last N lines, similar to tail -n. Defaults to ${DEFAULT_TAIL_LINES}. Use 0 for no limit.`,
    ),
});

/** Schema with background param included. */
export const executeCommandWithBackgroundSchema = executeCommandInputSchema.extend({
  background: z
    .boolean()
    .optional()
    .describe(
      'Run the command in the background. Returns a PID immediately instead of waiting for completion. Use get_process_output to check on it later.',
    ),
});

// Listed first so models write the summary before the command itself.
const descriptionShape = {
  description: z
    .string()
    .min(1)
    .describe(
      'Short plain-language description of what this command does, shown to the user in place of the raw command (5-10 words, e.g. "Running the auth unit tests"). Write it first, before `command` and the other arguments, so the user sees it while the rest of the call streams in. Write it as the next step in an ongoing narrative: it can build on your previous commands implicitly, e.g. after "Searching open PRs for failing CI" the next command can be "Drilling into the first of 15 failures".',
    ),
};

/** Base schema with a required leading `description` (used when `requireDescription` is set). */
export const executeCommandWithDescriptionSchema = z.object({
  ...descriptionShape,
  ...executeCommandInputSchema.shape,
});

/** Background schema with a required leading `description`. */
export const executeCommandWithDescriptionAndBackgroundSchema = z.object({
  ...descriptionShape,
  ...executeCommandWithBackgroundSchema.shape,
});

/**
 * Extract `| tail -N` or `| tail -n N` from the end of a command.
 * LLMs are trained to pipe to tail for long outputs, but this prevents streaming —
 * the user sees nothing until the command finishes. By stripping the tail pipe and
 * applying it programmatically afterward, all output streams in real time while
 * the final result sent to the model is still truncated.
 *
 * Returns the cleaned command and extracted tail line count (if any).
 */
function extractTailPipe(command: string): { command: string; tail?: number } {
  const match = command.match(/\|\s*tail\s+(?:-n\s+)?(-?\d+)\s*$/);
  if (match) {
    const lines = Math.abs(parseInt(match[1]!, 10));
    if (lines > 0) {
      return {
        command: command.replace(/\|\s*tail\s+(?:-n\s+)?-?\d+\s*$/, '').trim(),
        tail: lines,
      };
    }
  }
  return { command };
}

/** Format command streams consistently with get_process_output. */
function formatCommandOutput(stdout: string, stderr: string): string[] {
  const parts: string[] = [];

  if (stdout && stderr) {
    parts.push('stdout:', stdout, '', 'stderr:', stderr);
  } else if (stdout) {
    parts.push(stdout);
  } else if (stderr) {
    parts.push('stderr:', stderr);
  }

  return parts;
}

/** Append a terminal status after one blank line without altering streamed output. */
function appendTerminalLine(parts: string[], terminalLine: string): string {
  if (parts.length === 0) return terminalLine;

  const output = parts.join('\n');
  const separator = output.endsWith('\n\n') ? '' : output.endsWith('\n') ? '\n' : '\n\n';
  return `${output}${separator}${terminalLine}`;
}

/** Shared execute function used by both foreground-only and background-capable tool variants. */
async function executeCommand(input: Record<string, any>, context: any) {
  let { command, cwd, tail } = input;
  const timeout = input.timeout != null ? (input.timeout as number) * 1000 : undefined;
  const background = input.background as boolean | undefined;
  const { workspace, sandbox } = requireSandbox(context);

  // Extract tail pipe from command so output can stream in real time
  if (!background) {
    const extracted = extractTailPipe(command);
    command = extracted.command;
    // Extracted tail overrides schema tail param (explicit pipe intent takes priority)
    if (extracted.tail != null) {
      tail = extracted.tail;
    }
  }

  // Lazy browser launch and CDP URL injection for browser CLI commands
  const browser = workspace.browser;
  const { browserClis, usingExternalCdp, externalCdpUrl } = browserCliHandler.analyzeCommand(command);

  if (browser && browserClis.length > 0 && !usingExternalCdp) {
    const threadId = context?.agent?.threadId ?? context?.threadId ?? 'default';

    // Launch browser if not already running (for this thread if thread-scoped)
    if (!browser.isBrowserRunning(threadId)) {
      await browser.launch(threadId);
    }

    const cdpUrl = browser.getCdpUrl(threadId);
    const browserId = browser.id;

    if (cdpUrl) {
      // Run warmup commands for CLIs that need them
      const warmups = browserCliHandler.getWarmupCommands(browserId, browserClis, cdpUrl, threadId);
      for (const { cliName, command: warmupCmd } of warmups) {
        try {
          if (sandbox.executeCommand) {
            await sandbox.executeCommand(warmupCmd, [], { timeout: 10000 });
          }
          // Only mark as warmed up after successful warmup
          browserCliHandler.markWarmedUp(browserId, cliName, threadId);
          // Register cleanup when browser closes
          browserCliHandler.registerWarmupCleanup(browserId, cliName, threadId, browser);
        } catch {
          // Don't mark as warmed up - will retry on next command
          // This allows recovery if the CLI daemon wasn't ready
        }
      }

      // Inject CDP URL into all browser CLI commands in the chain
      command = browserCliHandler.injectCdpUrl(command, cdpUrl, threadId);
    }
  } else if (browser && browserClis.length > 0 && usingExternalCdp && externalCdpUrl) {
    // Agent is using their own external CDP - connect BrowserViewer to it for screencast
    const threadId = context?.agent?.threadId ?? context?.threadId ?? 'default';
    try {
      await browser.connectToExternalCdp(externalCdpUrl, threadId);
    } catch {
      // Non-fatal - agent can still use the external CDP, just no screencast
    }
  }

  await emitWorkspaceMetadata(context, WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND);
  const toolCallId = context?.agent?.toolCallId;
  const toolConfig = workspace.getToolsConfig()?.[WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND];
  const tokenLimit = toolConfig?.maxOutputTokens;
  const tokenFrom = 'sandwich' as const;

  const span = startWorkspaceSpan(context, workspace, {
    category: 'sandbox',
    operation: background ? 'spawnProcess' : 'executeCommand',
    input: { command, cwd, timeout: input.timeout, background },
    attributes: { sandboxProvider: sandbox.provider },
  });

  // Background mode: spawn via process manager and return immediately
  if (background) {
    if (!sandbox.processes) {
      const err = new SandboxFeatureNotSupportedError('processes');
      span.error(err);
      throw err;
    }

    const bgConfig = toolConfig?.backgroundProcesses;

    // Resolve abort signal: undefined = use context signal (from agent), null/false = disabled
    const bgAbortSignal =
      bgConfig?.abortSignal === undefined ? context?.abortSignal : bgConfig.abortSignal || undefined;

    // Use `let` so callbacks can reference handle.pid via closure.
    // spawn() resolves before any data events fire (Node event loop guarantees this).
    let handle: Awaited<ReturnType<typeof sandbox.processes.spawn>>;
    handle = await sandbox.processes.spawn(command, {
      cwd: cwd ?? undefined,
      timeout: timeout ?? undefined,
      abortSignal: bgAbortSignal,
      // A background process collects output rather than being driven over stdin,
      // so close stdin at spawn. Otherwise `rg`/`grep`/`cat` with no path argument
      // reads stdin and never exits.
      stdinMode: 'ignore',
      onStdout: bgConfig?.onStdout
        ? (data: string) => bgConfig.onStdout!(data, { pid: handle.pid, toolCallId })
        : undefined,
      onStderr: bgConfig?.onStderr
        ? (data: string) => bgConfig.onStderr!(data, { pid: handle.pid, toolCallId })
        : undefined,
    });

    // Wire exit callback (fire-and-forget). The observer runs after this tool has
    // already returned the PID, so any failure here has no live call frame to catch
    // it. Own the detached promise: await the callback so both synchronous throws and
    // rejected async callbacks land in the try/catch, and attach a terminal .catch()
    // for a rejected wait(), routing all failures to the logger so
    // neither escapes as a process-terminating unhandled rejection. Observation
    // failures are logged distinctly and never synthesize a successful exit.
    if (bgConfig?.onExit) {
      void handle
        .wait()
        .then(async result => {
          try {
            await bgConfig.onExit!({
              pid: handle.pid,
              exitCode: result.exitCode,
              stdout: result.stdout,
              stderr: result.stderr,
              stdoutTruncated: result.stdoutTruncated,
              stderrTruncated: result.stderrTruncated,
              stdoutDroppedBytes: result.stdoutDroppedBytes,
              stderrDroppedBytes: result.stderrDroppedBytes,
              toolCallId,
            });
          } catch (callbackError) {
            workspace.logger?.error('Background process onExit callback threw', {
              error: callbackError,
              pid: handle.pid,
              toolCallId,
            });
          }
        })
        .catch(observeError => {
          workspace.logger?.error('Failed to observe background process exit', {
            error: observeError,
            pid: handle.pid,
            toolCallId,
          });
        });
    }

    span.end({ success: true }, { pid: Number(handle.pid) || undefined });
    return `Started background process (PID: ${handle.pid})`;
  }

  // Foreground mode: execute and wait for completion
  if (!sandbox.executeCommand) {
    const err = new SandboxFeatureNotSupportedError('executeCommand');
    span.error(err);
    throw err;
  }

  const startedAt = Date.now();
  // Bounded copies used only on the error path, where the sandbox result is unavailable.
  // Unbounded accumulation here crashes the process with RangeError on very large output.
  const stdout = new RetainedOutputBuffer(DEFAULT_MAX_RETAINED_PROCESS_OUTPUT_BYTES);
  const stderr = new RetainedOutputBuffer(DEFAULT_MAX_RETAINED_PROCESS_OUTPUT_BYTES);
  try {
    const result = await sandbox.executeCommand(command, [], {
      timeout: timeout ?? undefined,
      cwd: cwd ?? undefined,
      abortSignal: context?.abortSignal, // foreground processes use agent's abort signal
      onStdout: async (data: string) => {
        stdout.append(data);
        await context?.writer?.custom({
          type: 'data-sandbox-stdout',
          data: { output: data, timestamp: Date.now(), toolCallId },
          transient: true,
        });
      },
      onStderr: async (data: string) => {
        stderr.append(data);
        await context?.writer?.custom({
          type: 'data-sandbox-stderr',
          data: { output: data, timestamp: Date.now(), toolCallId },
          transient: true,
        });
      },
    });

    await context?.writer?.custom({
      type: 'data-sandbox-exit',
      data: {
        exitCode: result.exitCode,
        success: result.success,
        executionTimeMs: result.executionTimeMs,
        killed: result.killed,
        timedOut: result.timedOut,
        toolCallId,
      },
    });

    span.end({ success: result.success }, { exitCode: result.exitCode });

    if (!result.success) {
      const parts = formatCommandOutput(
        await truncateOutput(result.stdout, tail, tokenLimit, tokenFrom),
        await truncateOutput(result.stderr, tail, tokenLimit, tokenFrom),
      );
      return appendTerminalLine(parts, `Exit code: ${result.exitCode}`);
    }

    return (
      formatCommandOutput(
        await truncateOutput(result.stdout, tail, tokenLimit, tokenFrom),
        await truncateOutput(result.stderr, tail, tokenLimit, tokenFrom),
      ).join('\n') || '(no output)'
    );
  } catch (error) {
    await context?.writer?.custom({
      type: 'data-sandbox-exit',
      data: {
        exitCode: -1,
        success: false,
        executionTimeMs: Date.now() - startedAt,
        toolCallId,
      },
    });
    span.end({ success: false }, { exitCode: -1 });
    const parts = formatCommandOutput(
      await truncateOutput(stdout.toString(), tail, tokenLimit, tokenFrom),
      await truncateOutput(stderr.toString(), tail, tokenLimit, tokenFrom),
    );
    const errorMessage = error instanceof Error ? error.message : String(error);
    return appendTerminalLine(parts, `Error: ${errorMessage}`);
  }
}

const baseDescription = `Execute a shell command in the workspace sandbox.

Examples:
  "npm install && npm run build"
  "ls -la src/"
  "cat config.json | jq '.database'"
  "cd /app && python main.py"

Usage:
- Commands run in a shell, so pipes, redirects, and chaining (&&, ||, ;) all work.
- Always quote file paths that contain spaces (e.g., cd "/path/with spaces").
- Use the timeout parameter (in seconds) to limit execution time. Behavior when omitted depends on the sandbox provider.
- Optionally use cwd to override the working directory. Commands run from the sandbox's configured workingDirectory (or the provider default) if omitted.`;

/** Foreground-only tool (no background param in schema). */
export const executeCommandTool = createTool({
  id: WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND,
  description: baseDescription,
  inputSchema: executeCommandInputSchema,
  outputSchema: z.string(),
  execute: executeCommand,
  toModelOutput: sandboxToModelOutput,
});

const backgroundDescription = `${baseDescription}

Set background: true to run long-running commands (dev servers, watchers) without blocking. You'll get a PID to track the process.`;

/** Tool with background param in schema (used when sandbox.processes exists). */
export const executeCommandWithBackgroundTool = createTool({
  id: WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND,
  description: backgroundDescription,
  inputSchema: executeCommandWithBackgroundSchema,
  outputSchema: z.string(),
  execute: executeCommand,
  toModelOutput: sandboxToModelOutput,
});

/** Foreground-only tool that requires a leading `description` arg. */
export const executeCommandWithDescriptionTool = createTool({
  id: WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND,
  description: baseDescription,
  inputSchema: executeCommandWithDescriptionSchema,
  outputSchema: z.string(),
  execute: executeCommand,
  toModelOutput: sandboxToModelOutput,
});

/** Background-capable tool that requires a leading `description` arg. */
export const executeCommandWithDescriptionAndBackgroundTool = createTool({
  id: WORKSPACE_TOOLS.SANDBOX.EXECUTE_COMMAND,
  description: backgroundDescription,
  inputSchema: executeCommandWithDescriptionAndBackgroundSchema,
  outputSchema: z.string(),
  execute: executeCommand,
  toModelOutput: sandboxToModelOutput,
});
